import { Extension, findParentNodeClosestToPos } from '@tiptap/core';
import type { Transaction } from '@tiptap/pm/state';
import { TableMap } from '@tiptap/pm/tables';

export type ColumnStyleClass = 'column-Total' | 'column-Highlight' | 'column-number';
export type RowStyleClass = 'row-Total';

type TableContext = {
    tablePos: number;
    tableStart: number;
    tableNode: any;
    cellPos: number;
};

const COLUMN_CLASSES = new Set<ColumnStyleClass>(['column-Total', 'column-Highlight', 'column-number']);
const ROW_CLASSES = new Set<RowStyleClass>(['row-Total']);

const splitClasses = (value: string | null | undefined): string[] =>
    value ? value.split(/\s+/).filter(Boolean) : [];

const addClass = (value: string | null | undefined, nextClass: string): string => {
    const classes = splitClasses(value);
    if (!classes.includes(nextClass)) {
        classes.push(nextClass);
    }
    return classes.join(' ');
};

const removeClasses = (value: string | null | undefined, removeSet: Set<string>): string => {
    const classes = splitClasses(value).filter((entry) => !removeSet.has(entry));
    return classes.join(' ');
};

const setNodeClass = (tr: Transaction, pos: number, updater: (value: string | null | undefined) => string): void => {
    const node = tr.doc.nodeAt(pos);
    if (!node) {
        return;
    }

    const updatedClass = updater(node.attrs['class'] as string | null | undefined);
    const nextAttrs = {
        ...node.attrs,
        class: updatedClass || null,
    };

    tr.setNodeMarkup(pos, undefined, nextAttrs);
};

const getTableContext = (tr: Transaction): TableContext | null => {
    const { selection } = tr;
    const cell =
        findParentNodeClosestToPos(selection.$from, (node) =>
            node.type.name === 'tableCell' || node.type.name === 'tableHeader',
        ) ?? null;
    const table =
        findParentNodeClosestToPos(selection.$from, (node) => node.type.name === 'table') ?? null;

    if (!cell || !table) {
        return null;
    }

    const tablePos = table.pos;
    const tableStart = tablePos + 1;
    const cellPos = cell.pos;

    return {
        tablePos,
        tableStart,
        tableNode: table.node,
        cellPos,
    };
};

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        tableStyle: {
            setColumnStyle: (style: ColumnStyleClass) => ReturnType;
            clearColumnStyles: () => ReturnType;
            setRowStyle: (style: RowStyleClass) => ReturnType;
            clearRowStyles: () => ReturnType;
        };
    }
}

export const TableStyle = Extension.create({
    name: 'tableStyle',

    addGlobalAttributes() {
        return [
            {
                types: ['tableCell', 'tableHeader'],
                attributes: {
                    class: {
                        default: null,
                        parseHTML: (element) => element.getAttribute('class'),
                        renderHTML: (attributes) =>
                            attributes['class'] ? { class: attributes['class'] } : {},
                    },
                },
            },
            {
                types: ['tableRow'],
                attributes: {
                    class: {
                        default: null,
                        parseHTML: (element) => element.getAttribute('class'),
                        renderHTML: (attributes) =>
                            attributes['class'] ? { class: attributes['class'] } : {},
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setColumnStyle:
                (style: ColumnStyleClass) =>
                    ({ tr, dispatch }) => {
                        const context = getTableContext(tr);
                        if (!context) {
                            return false;
                        }

                        const map = TableMap.get(context.tableNode);
                        const cellStart = context.cellPos - context.tableStart;
                        const rect = map.findCell(cellStart);

                        for (let row = 0; row < map.height; row += 1) {
                            const mapIndex = row * map.width + rect.left;
                            const cellPos = map.map[mapIndex];
                            if (cellPos == null) {
                                continue;
                            }

                            const absolutePos = context.tableStart + cellPos;
                            setNodeClass(tr, absolutePos, (value) => addClass(value, style));
                        }

                        if (dispatch) {
                            dispatch(tr);
                        }

                        return true;
                    },
            clearColumnStyles:
                () =>
                    ({ tr, dispatch }) => {
                        const context = getTableContext(tr);
                        if (!context) {
                            return false;
                        }

                        const map = TableMap.get(context.tableNode);
                        const cellStart = context.cellPos - context.tableStart;
                        const rect = map.findCell(cellStart);

                        for (let row = 0; row < map.height; row += 1) {
                            const mapIndex = row * map.width + rect.left;
                            const cellPos = map.map[mapIndex];
                            if (cellPos == null) {
                                continue;
                            }

                            const absolutePos = context.tableStart + cellPos;
                            setNodeClass(tr, absolutePos, (value) => removeClasses(value, COLUMN_CLASSES));
                        }

                        if (dispatch) {
                            dispatch(tr);
                        }

                        return true;
                    },
            setRowStyle:
                (style: RowStyleClass) =>
                    ({ tr, dispatch }) => {
                        const row = findParentNodeClosestToPos(
                            tr.selection.$from,
                            (node) => node.type.name === 'tableRow',
                        );

                        if (!row) {
                            return false;
                        }

                        setNodeClass(tr, row.pos, (value) => addClass(value, style));

                        if (dispatch) {
                            dispatch(tr);
                        }

                        return true;
                    },
            clearRowStyles:
                () =>
                    ({ tr, dispatch }) => {
                        const row = findParentNodeClosestToPos(
                            tr.selection.$from,
                            (node) => node.type.name === 'tableRow',
                        );

                        if (!row) {
                            return false;
                        }

                        setNodeClass(tr, row.pos, (value) => removeClasses(value, ROW_CLASSES));

                        if (dispatch) {
                            dispatch(tr);
                        }

                        return true;
                    },
        };
    },
});
